import { Select, Tag } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api, ApiRequestError } from '../lib/api.ts';
import { buildAirportOptions, type AirportOption } from '../lib/airports.ts';

interface Props {
  value?: AirportOption | null;
  onChange?: (value: AirportOption | null) => void;
  onRequiresLogin?: (reason: string) => void;
  placeholder?: string;
  id?: string;
  /** Airports this account searched before, shown first before anything is typed. */
  recent?: AirportOption[];
}

/**
 * Async airport search backed by /api/airports, with city grouping. Until the
 * user types, the account's previously used airports are offered first — they
 * come from the server, so they follow the account across devices.
 */
export function AirportSelect({ value, onChange, onRequiresLogin, placeholder = 'Search airport…', id, recent = [] }: Props) {
  const [options, setOptions] = useState<AirportOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(0);

  const runSearch = useCallback(
    async (q: string) => {
      const seq = ++latest.current;
      setQuery(q);
      setLoading(true);
      try {
        const data = await api.searchAirports(q);
        if (seq === latest.current) setOptions(buildAirportOptions(data));
      } catch (err) {
        if (err instanceof ApiRequestError && err.requiresLogin) onRequiresLogin?.(err.message);
      } finally {
        if (seq === latest.current) setLoading(false);
      }
    },
    [onRequiresLogin],
  );

  const onSearch = useCallback(
    (q: string) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void runSearch(q.trim()), 180);
    },
    [runSearch],
  );

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  // With no query typed, lead with the account's recent airports. Always keep the
  // currently selected option present so the Select can label it.
  const allOptions = useMemo(() => {
    const base = query ? options : [...recent, ...options.filter((o) => !recent.some((r) => r.key === o.key))];
    if (value && !base.some((o) => o.key === value.key)) return [value, ...base];
    return base;
  }, [options, value, recent, query]);

  return (
    <Select<string, AirportOption>
      id={id}
      showSearch
      allowClear
      value={value?.key ?? undefined}
      placeholder={placeholder}
      loading={loading}
      filterOption={false}
      onSearch={onSearch}
      onOpenChange={(open) => {
        if (open && !options.length) void runSearch('');
      }}
      onChange={(_key, option) => {
        const picked = Array.isArray(option) ? option[0] : option;
        onChange?.(picked ?? null);
      }}
      options={allOptions}
      fieldNames={{ label: 'name', value: 'key' }}
      optionRender={({ data }) => {
        const a = data as AirportOption;
        return (
          <div className="airport-option">
            <span>
              <strong>{a.name}</strong>
              {a.city || a.country ? (
                <span className="airport-option__meta">
                  {' · '}
                  {a.isCity ? a.country : [a.city, a.country].filter(Boolean).join(', ')}
                </span>
              ) : null}
            </span>
            <Tag color={a.isCity ? 'purple' : 'default'} style={{ marginInlineEnd: 0 }}>
              {a.isCity ? a.codes.join('/') : a.code}
            </Tag>
          </div>
        );
      }}
      labelRender={({ value: key }) => {
        const a = allOptions.find((o) => o.key === key);
        if (!a) return String(key);
        return (
          <span>
            {a.name} <Tag style={{ marginInlineStart: 6 }}>{a.codes.join('/')}</Tag>
          </span>
        );
      }}
      notFoundContent={loading ? 'Searching…' : 'No airports'}
      style={{ width: '100%' }}
    />
  );
}
